import type { SignInOutput } from 'aws-amplify/auth';

// Maps an Amplify v6 sign-in nextStep to the SPA route that handles it.
//
// As of 2026-05-23 the User Pool runs a CUSTOM_AUTH magic-link flow
// rather than Cognito's built-in TOTP/EMAIL MFA. After
// signIn(CUSTOM_WITH_SRP) validates the password on a new device, the
// nextStep is `CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE` — we route to
// /auth/check-email where the user waits for the magic link to be
// approved and confirmSignIn() runs on `approved`.
//
// On a remembered device, Cognito skips the custom challenge entirely
// and nextStep is `DONE`. The TOTP/MFA cases below are kept as null-
// returning safety branches in case the pool config ever drifts; in
// the new architecture they shouldn't fire.
export type NextStepRoute =
  | { path: string; state?: Record<string, unknown> }
  | {
      /** The sign-in was risk-flagged (reCAPTCHA low score), NOT a
       *  credential failure — the UI shows bot-flag guidance instead of
       *  "incorrect email or password" (task #28). */
      flagged: 'recaptcha-low-score';
    }
  | null;

export function isFlaggedRoute(
  route: NextStepRoute,
): route is { flagged: 'recaptcha-low-score' } {
  return route !== null && 'flagged' in route;
}

export function routeForNextStep(
  output: { nextStep: SignInOutput['nextStep'] },
): NextStepRoute {
  const next = output.nextStep;
  switch (next.signInStep) {
    case 'DONE':
      return { path: '/app' };

    case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE': {
      // CreateAuthChallenge surfaces the approval token via the public
      // challenge parameters. additionalInfo is Amplify v6's name for
      // that field. Without the token we can't poll for approval, so
      // route to a clear failure state.
      const info =
        (next as { additionalInfo?: Record<string, string | undefined> }).additionalInfo ?? {};
      // Bot-flag round from create-auth (task #28): no approval token — the
      // round exists only to carry this reason to the client.
      if (info['failureReason'] === 'RECAPTCHA_LOW_SCORE') {
        return { flagged: 'recaptcha-low-score' };
      }
      const token = info['token'];
      if (token === undefined || token.length === 0) {
        return null;
      }
      return { path: '/auth/check-email', state: { token } };
    }

    // The CUSTOM_AUTH flow replaces all of these, but Amplify can still
    // theoretically surface them if pool config drifts. Treat as
    // unsupported.
    case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
    case 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP':
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return null;

    default:
      return null;
  }
}
